import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {NgClass, NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Subscription} from 'rxjs';
import {PopupService} from '../../../services/popup.service';
import {
  SQL_Q1_OPTIONS,
  SQL_Q3_OPTIONS,
  SQL_Q4_OPTIONS,
  SQL_QUESTIONS,
  SqlAnswers,
  SqlProgressStep,
  SqlQualification,
  SqlQualificationService,
  SqlQuestionId,
  SqlState,
  sqlFinishedEarlyLabel,
  sqlIsRunning,
  sqlIsStopping,
  sqlTierClass,
  sqlTierLabel
} from '../../../services/sql-qualification.service';

/**
 * "Sales Qualify" guided process (modal). Rendered at the report root so the
 * overlay covers the profile header; opened from the AI Agent Tool Box tile or
 * the SQL card via SqlQualificationService.requestOpen().
 *
 *   intro    → what the agent will do, credits, Start
 *   running  → live steps (credits → profile → website → Google → scoring)
 *   input    → the questions the agent could not settle (+ overrides)
 *   result   → score, tier, breakdown with evidence, re-run / edit
 */
type WizardView = 'intro' | 'running' | 'input' | 'result';

@Component({
  selector: 'app-sql-qualify-wizard',
  standalone: true,
  imports: [NgIf, NgForOf, NgClass, FormsModule],
  templateUrl: './sql-qualify-wizard.component.html',
  styleUrl: './sql-qualify-wizard.component.scss'
})
export class SqlQualifyWizardComponent implements OnInit, OnChanges, OnDestroy {

  @Input() report: any;
  @Input() company: any;

  visible = false;
  state: SqlState = {enabled: null, qualification: null, creditCost: 3, loaded: false, loading: false, starting: false, saving: false};
  editing = false;

  readonly questions = SQL_QUESTIONS;
  readonly q1Options = SQL_Q1_OPTIONS;
  readonly q3Options = SQL_Q3_OPTIONS;
  readonly q4Options = SQL_Q4_OPTIONS;

  form = this.emptyForm();

  /** wall clock for the elapsed / remaining timer while a run is visible */
  now = Date.now();
  private clock: ReturnType<typeof setInterval> | null = null;

  private stateSub: Subscription | null = null;
  private openSub: Subscription | null = null;
  private boundCompanyId: number | null = null;

  constructor(
    private sql: SqlQualificationService,
    private popupService: PopupService,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.openSub = this.sql.openRequests$.subscribe(req => {
      if (Number(req.companyId) === Number(this.company?.id)) {
        this.open();
      }
    });
    this.bind();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['company'] || changes['report']) {
      this.bind();
    }
  }

  ngOnDestroy(): void {
    this.stateSub?.unsubscribe();
    this.openSub?.unsubscribe();
    this.stopClock();
  }

  // ------------------------------------------------------------ derived

  get q(): SqlQualification | null {
    return this.state.qualification;
  }

  get isRunning(): boolean {
    return sqlIsRunning(this.q);
  }

  get tierLabel(): string {
    return sqlTierLabel(this.q);
  }

  get isStopping(): boolean {
    return sqlIsStopping(this.q) || !!this.state.stopping;
  }

  get finishedEarlyLabel(): string {
    return sqlFinishedEarlyLabel(this.q);
  }

  /** Seconds since the worker started the run. */
  get elapsedSeconds(): number {
    const started = this.q?.started_at ? Date.parse(this.q.started_at) : NaN;
    return Number.isFinite(started) ? Math.max(0, Math.floor((this.now - started) / 1000)) : 0;
  }

  /** Seconds left in the research budget (null before the run starts). */
  get remainingSeconds(): number | null {
    const deadline = this.q?.deadline_at ? Date.parse(this.q.deadline_at) : NaN;
    return Number.isFinite(deadline) ? Math.max(0, Math.floor((deadline - this.now) / 1000)) : null;
  }

  get budgetMinutes(): number {
    return Math.round((this.q?.research_seconds ?? 300) / 60);
  }

  /** 0-100 share of the research budget used, for the progress bar. */
  get budgetUsedPct(): number {
    const total = this.q?.research_seconds ?? 300;
    return Math.min(100, Math.round((this.elapsedSeconds / total) * 100));
  }

  formatDuration(seconds: number | null): string {
    if (seconds === null) {
      return '--:--';
    }
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  get tierClass(): string {
    return sqlTierClass(this.q);
  }

  get view(): WizardView {
    if (!this.q || this.q.status === 'failed') {
      return 'intro';
    }
    if (this.isRunning) {
      return 'running';
    }
    if (this.editing || this.q.status === 'needs_input') {
      return 'input';
    }
    return 'result';
  }

  get steps(): SqlProgressStep[] {
    return this.q?.progress?.steps ?? [];
  }

  get answeredCount(): number {
    return this.q ? 4 - (this.q.pending_questions?.length ?? 0) : 0;
  }

  get pendingCount(): number {
    return this.q?.pending_questions?.length ?? 0;
  }

  /** Steps the intro previews before anything ran. */
  readonly plannedSteps = [
    {label: 'Reserve credits', detail: 'Refunded automatically if the run fails.'},
    {label: 'Read the report profile', detail: 'Apollo & Veridion descriptions, existing insights, pages already crawled.'},
    {label: 'Read the company website', detail: 'Pricing, products, solutions and about pages — only if the profile left questions open.'},
    {label: 'Search Google', detail: 'Targeted queries for whatever is still unknown, LinkedIn summary included.'},
    {label: 'Score the lead', detail: 'Deterministic points per answer, 0–100, with the evidence behind each point.'},
    {label: 'Your input', detail: 'Anything the agent could not settle with confidence is asked to you.'}
  ];

  stepIcon(step: SqlProgressStep): 'done' | 'running' | 'skipped' | 'waiting' | 'failed' | 'pending' {
    return step.status;
  }

  answerFor(q: SqlQuestionId): any {
    return this.q?.answers?.[q] ?? null;
  }

  isPending(q: SqlQuestionId): boolean {
    return !!this.q?.pending_questions?.includes(q);
  }

  answerLabel(q: SqlQuestionId): string {
    const a = this.answerFor(q);
    if (!a || !a.known) {
      return 'Unknown';
    }
    switch (q) {
      case 'q1':
        return a.resolved_value === 'mixed' ? `Mixed (mainly ${a.line})` : (a.resolved_value === 'solutions' ? 'Solutions & services' : 'Products');
      case 'q2':
        return a.value_usd ? `~$${Math.round(a.value_usd).toLocaleString()} / year (${a.line})` : 'Known';
      case 'q3':
        return a.resolved_value === 'yes' ? 'Yes' : 'No';
      case 'q4':
        return this.q4Options.find(o => o.value === a.resolved_value)?.label ?? a.resolved_value;
    }
  }

  sourceLabel(q: SqlQuestionId): string {
    const a = this.answerFor(q);
    if (!a || !a.known) {
      return '';
    }
    if (a.source === 'user') {
      return 'Answered by you';
    }
    const c = typeof a.confidence === 'number' ? ` · ${Math.round(a.confidence * 100)}% confidence` : '';
    return `Agent${c}`;
  }

  evidenceFor(q: SqlQuestionId): { url: string | null, quote: string, tier: string }[] {
    return this.answerFor(q)?.evidence ?? [];
  }

  // ------------------------------------------------------------ actions

  open(): void {
    this.visible = true;
    this.editing = false;
    this.fillForm();
    this.syncClock();
    if (this.report?.id && this.company?.id && !this.state.loaded) {
      this.sql.load(this.report.id, this.company.id);
    }
    this.cdr.markForCheck();
  }

  close(): void {
    this.visible = false;
    this.editing = false;
    this.stopClock();
  }

  /** Stop the research: the agent ends the current step and scores with what it has. */
  stopRun(): void {
    if (!this.report?.id || !this.company?.id || this.isStopping) {
      return;
    }

    this.sql.stop(this.report.id, this.company.id).subscribe({
      next: (qualification) => {
        if (!sqlIsRunning(qualification)) {
          // the worker was gone: the backend finished the run right away
          this.popupService.notify(
            qualification.status === 'failed'
              ? 'Stopped before anything was gathered — your credits were refunded.'
              : `Stopped. SQL score ${qualification.score} from what was gathered.`,
            'Sales Qualify', qualification.status === 'failed' ? 'warning' : 'success', 4000);
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        const status = error?.status;
        this.popupService.notify(
          status === 409 ? 'The run had already finished.' : (error?.error?.message || 'Could not stop the qualification.'),
          'Sales Qualify', status === 409 ? 'info' : 'error', 4000);
        if (status === 409) {
          this.sql.load(this.report.id, this.company.id);
        }
      }
    });
  }

  startEditing(): void {
    this.editing = true;
    this.fillForm();
  }

  start(force = false): void {
    if (!this.report?.id || !this.company?.id || this.state.starting) {
      return;
    }

    this.sql.start(this.report.id, this.company.id, force).subscribe({
      next: (data) => {
        this.editing = false;
        if (data.already_qualified) {
          return; // the result view shows it
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        const status = error?.status;
        const message = error?.error?.message
          || (status === 402 ? 'Not enough credits to qualify this lead.'
            : status === 409 ? 'A qualification is already in progress.'
              : 'Could not start the qualification.');
        this.popupService.notify(message, 'Sales Qualify', status === 409 ? 'warning' : 'error', 4000);
        if (status === 409) {
          this.sql.load(this.report.id, this.company.id);
        }
      }
    });
  }

  saveAnswers(): void {
    if (!this.report?.id || !this.company?.id || this.state.saving) {
      return;
    }

    const answers: SqlAnswers = {};
    const clear: string[] = [];
    const overrides = this.q?.user_overrides ?? {};

    (['q1', 'q3', 'q4'] as SqlQuestionId[]).forEach(id => {
      const v = (this.form as any)[id];
      if (v) {
        (answers as any)[id] = v;
      } else if ((overrides as any)[id]) {
        clear.push(id);
      }
    });

    if (this.form.q2_value && this.form.q2_value > 0) {
      answers.q2 = {value_usd: Number(this.form.q2_value)};
      if (this.form.q2_offering) {
        answers.q2.offering = this.form.q2_offering;
      }
    } else if (overrides.q2) {
      clear.push('q2');
    }

    if (clear.length) {
      answers.clear = clear;
    }

    if (Object.keys(answers).length === 0) {
      this.editing = false;
      return;
    }

    this.sql.answer(this.report.id, this.company.id, answers).subscribe({
      next: (qualification) => {
        this.editing = false;
        this.popupService.notify(`SQL score ${qualification.score} · ${sqlTierLabel(qualification)}`, 'Sales Qualify', 'success', 3000);
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.popupService.notify(error?.error?.message || 'Could not save your answers.', 'Sales Qualify', 'error', 4000);
      }
    });
  }

  // ------------------------------------------------------------ internals

  private bind(): void {
    const companyId = Number(this.company?.id);
    if (!companyId || companyId === this.boundCompanyId) {
      return;
    }
    this.boundCompanyId = companyId;
    this.stateSub?.unsubscribe();

    this.stateSub = this.sql.state$(companyId).subscribe(state => {
      const wasRunning = this.isRunning;
      this.state = state;
      if (wasRunning && !this.isRunning && this.visible) {
        this.fillForm();
      }
      this.syncClock();
      this.cdr.markForCheck();
    });

    if (this.report?.user_id) {
      this.sql.listen(Number(this.report.user_id));
    }
    if (this.report?.id) {
      this.sql.load(this.report.id, companyId);
    }
  }

  /** Tick once a second while the wizard is open on a running run. */
  private syncClock(): void {
    const shouldTick = this.visible && this.isRunning;
    if (shouldTick && !this.clock) {
      this.now = Date.now();
      this.clock = setInterval(() => {
        this.now = Date.now();
        this.cdr.markForCheck();
      }, 1000);
    } else if (!shouldTick) {
      this.stopClock();
    }
  }

  private stopClock(): void {
    if (this.clock) {
      clearInterval(this.clock);
      this.clock = null;
    }
  }

  private fillForm(): void {
    this.form = this.emptyForm();
    const o: any = this.q?.user_overrides ?? {};
    if (o.q1) {
      this.form.q1 = o.q1;
    }
    if (o.q2?.value_usd) {
      this.form.q2_value = o.q2.value_usd;
      this.form.q2_offering = o.q2.offering ?? '';
    }
    if (o.q3) {
      this.form.q3 = o.q3;
    }
    if (o.q4) {
      this.form.q4 = o.q4;
    }
  }

  private emptyForm() {
    return {q1: '', q2_value: null as number | null, q2_offering: '', q3: '', q4: ''};
  }
}
